import {
  Element,
  FirmClientElementVisibilityOverride,
  FirmElementCustomization,
} from '../generated/graphql';
import { SCORECARD_ELEMENT_SYMBOLS } from './constants';
import { ScorecardElementSymbol } from './util';

type FirmCustomizationOwner =
  | { elementCustomizations?: Pick<FirmElementCustomization, 'element' | 'visibleByDefault'>[] | null }
  | null
  | undefined;
type FirmClientCustomizationOwner =
  | { elementVisibilityOverrides?: Pick<FirmClientElementVisibilityOverride, 'element' | 'visible'>[] | null }
  | null
  | undefined;

const scorecardElementSymbolToElementMap: Partial<Record<ScorecardElementSymbol, Element>> = {
  Br: Element.Br,
  Bt: Element.Bt,
  Dr: Element.Dr,
  Er: Element.Er,
  Ir: Element.Ir,
  Es: Element.Es,
  Lt: Element.Lt,
  Qt: Element.Qt,
  Rt: Element.Rt,
  Sr: Element.Sr,
  Tr: Element.Tr,
  Tt: Element.Tt,
};

export function scorecardElementSymbolToElement(symbol: ScorecardElementSymbol): Element | undefined {
  return scorecardElementSymbolToElementMap[symbol];
}

export const CUSTOMIZABLE_SCORECARD_ELEMENT_SYMBOLS = SCORECARD_ELEMENT_SYMBOLS.filter(
  (symbol) => scorecardElementSymbolToElement(symbol) !== undefined
);

export function getFirmElementCustomization(
  firm: FirmCustomizationOwner,
  symbol: ScorecardElementSymbol
) {
  const element = scorecardElementSymbolToElement(symbol);
  if (!element) return undefined;

  return firm?.elementCustomizations?.find((customization) => customization.element === element);
}

export function getFirmClientElementVisibilityOverride(
  firmClient: FirmClientCustomizationOwner,
  symbol: ScorecardElementSymbol
) {
  const element = scorecardElementSymbolToElement(symbol);
  if (!element) return undefined;

  return firmClient?.elementVisibilityOverrides?.find((override) => override.element === element);
}

export function resolveScorecardElementVisibility(args: {
  allowFirmClientVisibilityOverrides?: boolean;
  firm: FirmCustomizationOwner;
  firmClient?: FirmClientCustomizationOwner;
  symbol: ScorecardElementSymbol;
}) {
  const firmClientOverride = args.allowFirmClientVisibilityOverrides
    ? getFirmClientElementVisibilityOverride(args.firmClient, args.symbol)
    : undefined;
  if (firmClientOverride?.visible !== undefined && firmClientOverride?.visible !== null) {
    return firmClientOverride.visible;
  }

  const firmVisible = getFirmElementCustomization(args.firm, args.symbol)?.visibleByDefault ?? true;
  return firmVisible;
}

export function getVisibleScorecardElementSymbols(args: {
  allowFirmClientVisibilityOverrides?: boolean;
  firm: FirmCustomizationOwner;
  firmClient?: FirmClientCustomizationOwner;
  symbols?: ScorecardElementSymbol[];
}) {
  return (args.symbols ?? SCORECARD_ELEMENT_SYMBOLS).filter((symbol) =>
    resolveScorecardElementVisibility({
      allowFirmClientVisibilityOverrides: args.allowFirmClientVisibilityOverrides,
      firm: args.firm,
      firmClient: args.firmClient,
      symbol,
    })
  );
}
